// ====================================================
// VidyaTrack — Profile & Settings Page
// ====================================================

import React, { useState } from "react";
import { FiUser, FiMail, FiPhone, FiSave, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { toast } from "react-toastify";

const ROLE_COLORS = {
  admin  : "#054bb4",
  teacher: "#059669",
  student: "#7c3aed",
  parent : "#d97706",
};
const ROLE_LABELS = {
  admin  : "Administrator",
  teacher: "Teacher",
  student: "Student",
  parent : "Parent",
};

export default function ProfilePage() {
  const { userData, updateUserData } = useAuth();
  const { dark } = useTheme();

  const [name,   setName]   = useState(userData?.name  || "");
  const [phone,  setPhone]  = useState(userData?.phone || "");
  const [saving, setSaving] = useState(false);

  const [curPw,  setCurPw]  = useState("");
  const [newPw,  setNewPw]  = useState("");
  const [conPw,  setConPw]  = useState("");
  const [showCur,setShowCur]= useState(false);
  const [showNew,setShowNew]= useState(false);

  const roleColor = ROLE_COLORS[userData?.role] || "#054bb4";
  const initials  = (userData?.name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      await updateUserData({ name: name.trim(), phone: phone.trim() });
      toast.success("Profile updated successfully! ✅");
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (!curPw)              { toast.error("Enter your current password"); return; }
    if (newPw.length < 6)   { toast.error("New password must be at least 6 characters"); return; }
    if (newPw !== conPw)     { toast.error("Passwords do not match"); return; }
    // Firebase password change requires reauthentication — show guidance
    toast.info("To change your password, use the 'Forgot Password' link on the login page. Firebase requires re-authentication for security.", { autoClose: 8000 });
    setCurPw(""); setNewPw(""); setConPw("");
  };

  const cardBg  = dark ? "#1a2535" : "#fff";
  const inputBg = dark ? "#111827" : "#fff";
  const textClr = dark ? "#e2e8f0" : "#111827";
  const subClr  = dark ? "#94a3b8" : "#6b7280";

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your account settings</p>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:20, alignItems:"start" }}>

        {/* ── Profile card ── */}
        <div style={{ background:cardBg, borderRadius:16, border:"1px solid var(--border)", padding:"28px 20px", textAlign:"center", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
          <div style={{ width:72, height:72, borderRadius:"50%", background:roleColor, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.6rem", fontWeight:800, margin:"0 auto 16px", boxShadow:`0 4px 14px ${roleColor}40` }}>
            {initials}
          </div>
          <h3 style={{ fontSize:"1.05rem", fontWeight:700, color:textClr, marginBottom:6 }}>{userData?.name}</h3>
          <div style={{ display:"inline-block", background:roleColor, color:"#fff", fontSize:".68rem", fontWeight:700, padding:"2px 12px", borderRadius:20, textTransform:"uppercase", marginBottom:16, letterSpacing:".04em" }}>
            {ROLE_LABELS[userData?.role] || userData?.role}
          </div>
          <div style={{ fontSize:".82rem", color:subClr, lineHeight:2 }}>
            <div>📧 {userData?.email}</div>
            {userData?.phone && <div>📞 {userData.phone}</div>}
            {userData?.school && <div>🏫 {userData.school}</div>}
            {userData?.subject && <div>📚 {userData.subject}</div>}
            {userData?.className && <div>🎓 {userData.className}{userData?.section ? ` - ${userData.section}` : ""}</div>}
            {userData?.childName && <div>👦 Monitoring: {userData.childName}</div>}
          </div>
        </div>

        {/* ── Right column ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:18 }}>

          {/* Personal Info */}
          <div style={{ background:cardBg, borderRadius:16, border:"1px solid var(--border)", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
            <div style={{ padding:"16px 22px", borderBottom:"1px solid var(--border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ fontSize:".93rem", fontWeight:700, color:textClr }}>Personal Information</h3>
            </div>
            <div style={{ padding:"20px 22px" }}>
              <form onSubmit={handleSaveProfile}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                  {/* Full name */}
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <div style={{ position:"relative" }}>
                      <FiUser style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"var(--gray-400)", pointerEvents:"none" }} size={14}/>
                      <input
                        className="form-control"
                        style={{ paddingLeft:36, background:inputBg, color:textClr }}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  {/* Phone */}
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <div style={{ position:"relative" }}>
                      <FiPhone style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"var(--gray-400)", pointerEvents:"none" }} size={14}/>
                      <input
                        className="form-control"
                        style={{ paddingLeft:36, background:inputBg, color:textClr }}
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        placeholder="+977-98XXXXXXXX"
                      />
                    </div>
                  </div>
                  {/* Email (read-only) */}
                  <div className="form-group" style={{ gridColumn:"1/-1" }}>
                    <label className="form-label">Email Address <span style={{ fontWeight:400, textTransform:"none", fontSize:".72rem", color:"var(--gray-400)" }}>(cannot be changed)</span></label>
                    <div style={{ position:"relative" }}>
                      <FiMail style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"var(--gray-400)", pointerEvents:"none" }} size={14}/>
                      <input
                        className="form-control"
                        style={{ paddingLeft:36, background:dark?"#0f172a":"var(--gray-100)", cursor:"not-allowed", color:subClr }}
                        value={userData?.email || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? <><div className="spinner spinner-sm"/> Saving…</> : <><FiSave size={13}/> Save Changes</>}
                </button>
              </form>
            </div>
          </div>

          {/* Change Password */}
          <div style={{ background:cardBg, borderRadius:16, border:"1px solid var(--border)", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
            <div style={{ padding:"16px 22px", borderBottom:"1px solid var(--border)" }}>
              <h3 style={{ fontSize:".93rem", fontWeight:700, color:textClr }}>Change Password</h3>
            </div>
            <div style={{ padding:"20px 22px" }}>
              {/* Security note */}
              <div style={{ background:dark?"#0f2040":"#eff6ff", border:"1px solid #bfdbfe", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:".82rem", color:"#1d4ed8", display:"flex", gap:8 }}>
                <span>🔒</span>
                <span>For security, password changes require you to sign out and use the <strong>Forgot Password</strong> link on the login page.</span>
              </div>
              <form onSubmit={handleChangePassword}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:14 }}>
                  {[
                    { label:"Current Password", val:curPw, set:setCurPw, show:showCur, toggle:()=>setShowCur(v=>!v) },
                    { label:"New Password",      val:newPw, set:setNewPw, show:showNew, toggle:()=>setShowNew(v=>!v) },
                    { label:"Confirm Password",  val:conPw, set:setConPw, show:false,   toggle:null },
                  ].map(f => (
                    <div key={f.label} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <div style={{ position:"relative" }}>
                        <FiLock style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"var(--gray-400)", pointerEvents:"none" }} size={13}/>
                        <input
                          type={f.show ? "text" : "password"}
                          className="form-control"
                          style={{ paddingLeft:34, paddingRight:f.toggle?34:12, background:inputBg, color:textClr }}
                          placeholder="••••••••"
                          value={f.val}
                          onChange={e => f.set(e.target.value)}
                        />
                        {f.toggle && (
                          <button type="button" onClick={f.toggle} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--gray-400)", display:"flex", alignItems:"center" }}>
                            {f.show ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button type="submit" className="btn btn-outline btn-sm">
                  <FiLock size={13}/> Update Password
                </button>
              </form>
            </div>
          </div>

          {/* Account Info */}
          <div style={{ background:cardBg, borderRadius:16, border:"1px solid var(--border)", padding:"16px 22px", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
            <h3 style={{ fontSize:".93rem", fontWeight:700, color:textClr, marginBottom:12 }}>Account Information</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:".84rem" }}>
              {[
                { label:"Role",       value: ROLE_LABELS[userData?.role] || userData?.role },
                { label:"User ID",    value: userData?.uid?.slice(0,16) + "…" },
                { label:"School",     value: userData?.school || "—" },
                { label:"Status",     value: userData?.isActive !== false ? "✅ Active" : "❌ Inactive" },
              ].map(i => (
                <div key={i.label} style={{ background:dark?"#111827":"var(--gray-50)", borderRadius:9, padding:"10px 14px", border:"1px solid var(--border)" }}>
                  <div style={{ fontSize:".7rem", fontWeight:700, color:subClr, textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>{i.label}</div>
                  <div style={{ fontWeight:500, color:textClr, fontSize:".88rem" }}>{i.value}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>


    </div>
  );
}
